import { useEffect, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'

import { NormalizedSwapFormValues, PageState, PageStatus, SwapFormFields } from '../types'
import { SwapFormSchema } from './form/schema'
import { useSwapForm } from './form/useSwapForm'
import { getChainConfigEntry } from '@/config/chain'
import { usePageChainId } from '@/domain/hooks/usePageChainId'
import { useConnectModal } from '@/domain/hooks/wallet'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { SwapObjective } from '@/features/actions/flavours/swap/types'
import { Objective } from '@/features/actions/logic/types'
import { useActions } from '@/features/actions/logic/useActions'
import { ActionsViewState } from '@/features/actions/types'
import { useSwapTokenRepository } from '@/features/swap/logic/useSwapTokenRepository'

export interface UseSwapResult {
  formFields: SwapFormFields
  formValues: NormalizedSwapFormValues
  form: UseFormReturn<SwapFormSchema>
  objectives: Objective[]
  pageStatus: PageStatus
  actionsViewState: ActionsViewState
  isConnected: boolean
  openConnectModal: () => void
}

export function useSwap(): UseSwapResult {
  const { chainId } = usePageChainId()
  const chainConfig = getChainConfigEntry(chainId)
  const { account } = useConnectedAddress({ throwOnNotConnected: false })
  const { openConnectModal = () => {} } = useConnectModal()

  const tokenRepository = useSwapTokenRepository({ chainId, account })

  const [pageStatus, setPageStatus] = useState<PageState>('form')
  const { form, formValues, formFields, isDebouncing, isFormValid } = useSwapForm({
    tokenRepository,
    psmStables: chainConfig.savings?.psmStables,
  })
  const objectives: SwapObjective[] = [
    {
      type: 'swap',
      ...formValues,
    },
  ]
  const actionsEnabled = formValues.amount.gt(0) && isFormValid && !isDebouncing

  const { viewState: actionsViewState, reset: resetActions } = useActions({
    objectives,
    context: {
      tokenRepository,
      chainId,
    },
    onFinish: () => setPageStatus('success'),
    enabled: actionsEnabled,
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want to reset actions when the form values change
  useEffect(() => {
    resetActions()
  }, [formValues.from.symbol, formValues.to.symbol, formValues.amount.toString(), resetActions])

  return {
    form,
    formFields,
    formValues,
    objectives,
    isConnected: !!account,
    openConnectModal,
    pageStatus: {
      state: pageStatus,
      actionsEnabled,
      submitForm: () => setPageStatus('confirmation'),
      restartSwap: () => {
        form.reset()
        resetActions()
        setPageStatus('form')
      },
      goToSuccessScreen: () => setPageStatus('success'),
    },
    actionsViewState,
  }
}
